/*
user.service.js

-GE CONFIDENTIAL-
Type: Source Code

Copyright (c) 2021, GE Healthcare Co.
All Rights Reserved

This unpublished material is proprietary to GE Healthcare. The methods and
techniques described herein are considered trade secrets and/or
confidential. Reproduction or distribution, in whole or in part, is
forbidden except by express written permission of GE Healthcare.
*/
define('modules/shell/services/user.service',[
    'lodash',
    '../module'
], function (_, module) {
    'use strict';

    module.service('ShellUserService', ['$http', '$q', function ($http, $q) {

        var UserService = this;
        var user = null;
        var uomEndpoint = null;
        var deferred = null;
        var appAccess = [];

        var getUOMEndpoint = function () {
            if (!uomEndpoint) {
                uomEndpoint = JSON.parse(sessionStorage.getItem('X-Endpoints'));
                uomEndpoint = uomEndpoint.apiGatewayUrl;
            }

            return uomEndpoint;
        };

        var hasPortalAccess = function (user) {
            if (user.portalAccess === null || user.isPatient) {
                return true;
            }
            var portalAccess = user.portalAccess || [];
            return portalAccess.some(function(org){return !!org.hasAccess;});
        };

        var hasCEAccess = function (user) {
            return user.organization.some(function(org) {return !!org.hasCaseExchangeAccess;});
        };

        var hasMDTAccess = function (user) {
            return user.organization.some(function(org){return !!org.hasMdtAccess;});
        };

        var hasRole = function (roles, roleName) {
            return !!roles && roles.some( function(r) {return r.role.name === roleName;} );
        };

        this.reset = function () {
            appAccess = [];
        };

        this.getC360UsersData = function () {
            var deferred = $q.defer();

            $http.get(getUOMEndpoint() + '/v1/umgmt/users/me', {
                headers: {
                    'Content-type': 'application/json'
                },
                data: null
            }).then(function (response) {
                var c360User = response.data;
                var c360EntitiyRoles = c360User ? c360User.entityRoles : '';
                c360User.isGeAdmin = hasRole(c360EntitiyRoles, 'GE_ADMIN');
                c360User.isGeAccountManager = hasRole(c360EntitiyRoles, 'GE_ACCOUNT_MANAGER');
                c360User.isDefaultRole = hasRole(c360EntitiyRoles, 'DEFAULT_ROLE');
                if (hasCEAccess(c360User)) {
                    appAccess.push('CE');
                }
                c360User.hasCEAccess = hasCEAccess(c360User);
                if (hasMDTAccess(c360User)) {
                    appAccess.push('MDT');
                }
                c360User.hasMDTAccess = hasMDTAccess(c360User);
                deferred.resolve(c360User);
            }).catch(function (response) {
                deferred.reject(response);
            });

            return deferred.promise;
        };

        this.getUserName = function () {
            deferred = $q.defer();
            $http.get(getUOMEndpoint() + '/v2/user/me', {
                params: {
                    orgrole: false,
                    detailed: false
                },
                headers: {
                    'Content-type': 'application/json'
                },
                data: null
            }).then(function (response) {
                deferred.resolve(response.data);
            }).catch(function (response) {
                deferred.reject(response);
                deferred = null;
            });

            return deferred.promise;
        };

        this.getUserData = function () {
            if (user) {
                return $q.when(user);
            }

            if (deferred) {
                return deferred.promise;
            }

            deferred = $q.defer();

            $http.get(getUOMEndpoint() + '/v2/user/me', {
                params: {
                    orgrole: false,
                    detailed: true
                },
                headers: {
                    'Content-type': 'application/json'
                },
                data: null
            }).then(function (response) {
                user = response.data;
                if (user.userEntityAppRoles) {
                    var userRoles = user.userEntityAppRoles.reduce(function (roles, ear) {
                        return roles.concat(ear.roles || []);
                    }, []);
                    user.isPatient = _.find(userRoles, { code: 'patient' }) !== undefined;
                    user.isAdmin = _.find(userRoles, function (role) { return /^.*admin$/.test(role.code); }) !== undefined;
                    user.isOrgLevelAccessUser = _.find(userRoles, { code: 'OrgLevelAccessUser' }) !== undefined;
                    user.isOnlyOrgLevelAccessUser = user.isOrgLevelAccessUser && _.find(userRoles, function (role) { return role.code !== 'OrgLevelAccessUser'; }) === undefined;
                    user.hasPortalAccess = hasPortalAccess(user);
                    if (user.hasPortalAccess) {
                        appAccess.push('EDP');
                    }
                    user.siteAdmin = _.find(userRoles, { code: 'siteadmin' }) !== undefined;
                    user.orgAdmin = _.find(userRoles, { code: 'orgadmin' }) !== undefined;
                    user.geAdmin = _.find(userRoles, { code: 'GE_ADMIN' }) !== undefined;
                    user.geAccountManager = _.find(userRoles, { code: 'GE_ACCOUNT_MANAGER' }) !== undefined;
                    user.isMDTCoordinator = _.find(userRoles, { code: 'groupadmin' }) !== undefined;

                    // New if block added to show patient based menu to OrgLevelAccess user who also happens to be patient(self registered patient)
                    if(user.isOrgLevelAccessUser && !user.isOnlyOrgLevelAccessUser){
                        var caseExchangeAppRoles = _.filter(user.userEntityAppRoles);
                        _.forEach(caseExchangeAppRoles, function (appRole) {
                            _.forEach(appRole.roles, function (role, index) {
                                if(role.code === 'OrgLevelAccessUser'){
                                    appRole.roles.splice(index, 1);
                                }
                            });
                        });
                    }
                }
                deferred.resolve(user);
            }).catch(function (response) {
                deferred.reject(response);
                deferred = null;
                user = null;
            });

            return deferred.promise;
        };

        this.doesUserHaveRole = function (role) {
            if (!user) {
                throw new Error('User data not loaded');
            }

            function checkUserRoleAvailability(r) {
                if (role === 'siteadmin') {
                    return r.code === role && r.status !== 'pending';
                }
                else {
                    return r.code === role;
                }
            }

            var entityRoles = user.userEntityAppRoles || [];
            for (var i = 0; i < entityRoles.length; i++) {
                var found = entityRoles[i].roles.filter(checkUserRoleAvailability)[0];

                if (found) {
                    return true;
                }
            }

            return false;
        };

        this.doesUserHaveAnyRoles = function (roles) {
            roles = roles || [];
            if (!Array.isArray(roles)) {
                throw new Error('roles is not an array');
            }

            for (var i = 0; i < roles.length; i++) {
                var found = UserService.doesUserHaveRole(roles[i]);

                if (found) {
                    return true;
                }
            }

            return false;
        };

        this.doesUserHaveAppAccess = function (apps) {
            if(!apps) {
                return true;
            }
            return apps.some(function (app) {
                return appAccess.indexOf(app) >= 0;
            });
        };

    }]);

});

